// The op-graph: one chip per pipeline step. Click a step to inspect the frame
// as-of that step; the insert affordance appears only adjacent to the step
// you're currently inspecting.
import { PiX } from 'react-icons/pi'
import { FRAMES } from '../frames'
import { chipText } from '../pipeline-compile'
import { FrameId, Op } from '../pipeline-types'
import { Action } from './store'
import { cn } from './ui'

interface Props {
  ops: Op[]
  frame: FrameId // the base table the chain runs against (labels the source chip)
  eff: number // inspected step index (0 = base table, n = after op n)
  insertAt: number | null
  canAppend: boolean // false once the frame resolves to a terminal Metric
  dispatch: (a: Action) => void
}

const chip = 'rounded-full border px-2.5 py-1 font-mono text-xs'

function InsertButton({
  at,
  insertAt,
  label,
  dispatch,
}: {
  at: number
  insertAt: number | null
  label: string
  dispatch: Props['dispatch']
}) {
  return (
    <button
      type="button"
      title="add a step here"
      onClick={() => dispatch({ t: 'OPEN_MENU', at })}
      className={cn(
        chip,
        'border-dashed',
        insertAt === at
          ? 'border-brand bg-surface-active text-brand'
          : 'border-line text-fg-secondary hover:border-brand hover:text-brand'
      )}
    >
      {label}
    </button>
  )
}

export function Pipeline({
  ops,
  frame,
  eff,
  insertAt,
  canAppend,
  dispatch,
}: Props) {
  const stepChip = (active: boolean) =>
    cn(
      chip,
      'cursor-pointer',
      active
        ? 'border-brand bg-surface-active text-brand'
        : 'border-line bg-surface text-fg hover:border-brand'
    )

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      <button
        type="button"
        title="view raw table"
        onClick={() => dispatch({ t: 'SET_CURSOR', cursor: 0 })}
        className={stepChip(eff === 0)}
      >
        {FRAMES[frame].label}
      </button>
      {eff === 0 && (
        <InsertButton
          at={0}
          insertAt={insertAt}
          label="+ add operation"
          dispatch={dispatch}
        />
      )}

      {ops.map((op, i) => (
        <span key={i} className="flex items-center gap-1.5">
          <span className="text-xs text-fg-muted">→</span>
          <button
            type="button"
            title="view frame after this step"
            onClick={() => dispatch({ t: 'SET_CURSOR', cursor: i + 1 })}
            className={stepChip(eff === i + 1)}
          >
            {chipText(op, frame)}
            <span
              className="ml-1.5 cursor-pointer text-fg-muted hover:text-error"
              onClick={(e) => {
                e.stopPropagation()
                dispatch({ t: 'REMOVE_OP', i })
              }}
            >
              <PiX size={11} className="inline" />
            </span>
          </button>
          {eff === i + 1 && (i + 1 < ops.length || canAppend) && (
            <InsertButton
              at={i + 1}
              insertAt={insertAt}
              label={i + 1 >= ops.length ? '+ add operation' : '+ insert step'}
              dispatch={dispatch}
            />
          )}
        </span>
      ))}

      {eff < ops.length && (
        <span className="ml-2 text-xs text-brand">
          ▲ viewing step {eff} of {ops.length} ·{' '}
          <button
            type="button"
            className="underline"
            onClick={() => dispatch({ t: 'SET_CURSOR', cursor: null })}
          >
            jump to end
          </button>
        </span>
      )}
    </div>
  )
}
